import { useId } from "react";
// HeadFlair — ozdoby na hlavu, ktoré sa nosia na profilovej fotke.
//
// PREČO TENTO SÚBOR EXISTUJE (§6a bod 5 — reuse-first):
// Ozdoby používajú PRESNE ten istý mechanizmus ako milestone flairy:
// backend ich predáva ako položky Obchodu s kódom `flair_*`, `/api/shop/equip` ich
// uloží do `users.equipped_flair` a `Avatar` ich vykreslí. Žiadny druhý systém.
// Nový je len OBSAH — kresba. Aby sa 23 kresieb nemuselo lepiť do `Avatar.jsx` ani
// do `BuzerIcons.tsx` (tam sú 24×24 ikony pre UI, nie ozdoby zarovnané na hlavu),
// žije katalóg tu a Avatar aj Obchod ho volajú tým istým komponentom.
//
// SÚRADNICOVÝ SYSTÉM (kľúčové, ak pridávaš ďalšiu ozdobu):
// viewBox je `0 0 130 130` a **kruh avatara je stred (65,65), polomer 50** —
// teda temeno hlavy je na y = 15, ľavý okraj x = 15, pravý x = 115. Ozdoby sa
// kreslia v tomto systéme, takže sedia na hlavu v KAŽDEJ veľkosti avatara
// (18–96 px, pozri grep `<Avatar`): SVG je 130 % veľkosti avatara, posunuté
// o −15 % — nič nie je v pevných pixeloch, všetko sa škáluje spolu s ním.
//
// FARBY (§6a bod 1 a 3): každá ozdoba má `tone` = **token**, kresba je line-art
// v `currentColor` s jemnou výplňou `fill-opacity`. Preto funguje vo všetkých
// 24 farebných témach aj v dark mode — žiadny surový odtieň, žiadne emoji,
// žiadne externé obrázky.
//
// VRSTVY (v30 „svätožiara navrch"): ozdoba na hlavu je slot **head**
// (`users.equipped_flair`) — presne jedna naraz. Svätožiara je samostatný slot
// **over** (`users.equipped_over`) a kreslí sa NAD ozdobou, takže sa dá nosiť
// napr. Mikulášska čiapka *a* svätožiara súčasne (pozri `Avatar.jsx`).
// Katalóg nižšie obsahuje OBA sloty — položka so `slot: "over"` (svätožiara) sa kreslí
// navrch. Vďaka tomu má aj ona ukážku v Obchode úplne rovnako ako ozdoby na hlavu.

const HEAD = { size: 130, cx: 65, cy: 65, r: 50 };

// v30 — `scale` v katalógu zmenší kresbu okolo bodu, kde SEDÍ na hlave (65, 40): okraj ostane
// na temene a ubudne smerom hore. Preto sa dá zmenšiť čiapka bez toho, aby začala lietať.
// (Momentálne ju NEPOUŽÍVA žiadna ozdoba — Mikulášska a zimná čiapka majú lem zámerne VIDITEĽNÝ
// na fotke, nech vyzerajú oblečené. Mechanizmus nechávam, je to jedno slovo v kresbe.)
// Prvok označený `{behind}` v kresbe sa oreže maskou hlavy — vyzerá, akoby prechádzal ZA
// fotkou (napr. biely lem Mikulášskej čiapky). Kruh masky je v lokálnych súradniciach
// scale-skupiny: (65,65)/r=50 spätne cez pivot (65,40), inak by ho `scale` posunul.
const headMask = (id, s) => {
  const k = s && s !== 1 ? s : 1;
  const cy = 40 + (HEAD.cy - 40) / k;
  const r = HEAD.r / k;
  return `<defs><mask id="${id}" maskUnits="userSpaceOnUse">`
    + `<rect x="-120" y="-120" width="400" height="400" fill="white"/>`
    + `<circle cx="${HEAD.cx}" cy="${cy.toFixed(2)}" r="${r.toFixed(2)}" fill="black"/>`
    + `</mask></defs>`;
};

// Dúhová výplň (jednorožcov roh) — prechod cez existujúce sémantické tokeny, takže drží
// aj v dark mode a nezavádza surové farby. `{grad}` v kresbe sa nahradí odkazom naň.
const rainbowDef = (id) =>
  `<defs><linearGradient id="${id}" x1="0" y1="1" x2="1" y2="0">`
  + `<stop offset="0" stop-color="var(--destructive)"/>`
  + `<stop offset=".28" stop-color="var(--stat-sun)"/>`
  + `<stop offset=".52" stop-color="var(--success)"/>`
  + `<stop offset=".76" stop-color="var(--info)"/>`
  + `<stop offset="1" stop-color="var(--type-sutaz)"/>`
  + `</linearGradient></defs>`;

const body = (def, maskId) => {
  const needsMask = def.svg.includes("{behind}");
  const gradId = `${maskId}g`;
  const svg = def.svg
    .replace(/\{behind\}/g, needsMask ? `mask="url(#${maskId})"` : "")
    .replace(/\{grad\}/g, `url(#${gradId})`);
  return (needsMask ? headMask(maskId, def.scale) : "")
    + (def.rainbow ? rainbowDef(gradId) : "")
    + scaled(svg, def.scale, def.lift);
};

const scaled = (svg, s, lift) => {
  const hasS = s && s !== 1, hasL = lift && lift !== 0;
  if (!hasS && !hasL) return svg;
  // poradie: najprv posun hore (`lift`), potom zmenšenie okolo bodu dosadnutia (65, 40)
  const tr = `${hasL ? `translate(0 ${-lift}) ` : ""}${hasS ? `translate(65 40) scale(${s}) translate(-65 -40)` : ""}`.trim();
  return `<g transform="${tr}">${svg}</g>`;
};


// tone = sémantický token; svg = obsah v súradniciach HEAD (viewBox 0 0 130 130).
export const HEAD_FLAIRS = {
  // slot OVER — nosí sa NAD ozdobou na hlavu. Kreslené ako perspektívny prstenec:
  // dva sústredné oblúky (predná hrana svetlejšia, zadná tenšia) namiesto plochého
  // krúžku s tvrdým box-shadow, ktorý pôsobil lacno.
  flair_halo: {
    label: "Svätožiara", tone: "var(--tier-gold)", slot: "over",
    // `glow` = svetelná aura navyše (viď HeadFlair nižšie). Svätožiara je jediná ozdoba,
    // ktorá má SVIETIŤ — ostatné sú kresby, tu je svetlo súčasťou významu.
    glow: true,
    // Rozostrený závoj pod prstencom (široký ťah s nízkou nepriehľadnosťou) dodá žiaru aj
    // tam, kde CSS filter nestačí — a drží sa v tom istom súradnicovom systéme.
    svg: '<path d="M34 19 a31 9 0 0 0 62 0" fill="none" stroke="currentColor" stroke-width="13" stroke-linecap="round" opacity=".18"/>'
       + '<ellipse cx="65" cy="19" rx="31" ry="9" fill="none" stroke="currentColor" stroke-width="8" opacity=".14"/>'
       + '<ellipse cx="65" cy="19" rx="31" ry="9" fill="none" stroke="currentColor" stroke-width="2" opacity=".45"/>'
       + '<path d="M34 19 a31 9 0 0 0 62 0" fill="none" stroke="currentColor" stroke-width="5.5" stroke-linecap="round"/>'
       // biele jadro: rozžeraví prstenec zvnútra, zlato ostáva len po okrajoch
       + '<path d="M34 19 a31 9 0 0 0 62 0" fill="none" stroke="var(--flair-shine)" stroke-width="2.4" stroke-linecap="round" opacity=".92"/>'
       + '<path d="M41 15.5 a24 6 0 0 1 48 0" fill="none" stroke="var(--flair-shine)" stroke-width="1.8" stroke-linecap="round" opacity=".7"/>',
  },
  // ── čiapky a klobúky ───────────────────────────────────────────────────────
  // Korunka bola pôvodne emoji 👑 (kind "cosmetic"). Od v30 je to riadna ozdoba na
  // hlavu v rovnakom line-art štýle ako zvyšok katalógu — kód `flair_crown` sa
  // ZÁMERNE nemení, aby ju ľudia, ktorí ju už vlastnia, mali ďalej a bez doplácania.
  flair_crown: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Korunka",
    tone: "var(--tier-gold)",
    svg:
      '<path d="M32 38 L32 13 L48 27 L65 5 L82 27 L98 13 L98 38 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M24 38 Q65 30 106 38 Q65 48 24 38 Z" fill="currentColor" fill-opacity=".78"/>' +
      '<circle cx="32" cy="11" r="4" fill="var(--flair-shine)" fill-opacity=".92"/>' +
      '<circle cx="65" cy="3" r="4.5" fill="var(--flair-shine)" fill-opacity=".92"/>' +
      '<circle cx="98" cy="11" r="4" fill="var(--flair-shine)" fill-opacity=".92"/>' +
      '<circle cx="65" cy="39" r="4.5" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>',
  },
  flair_santa: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Mikulášska čiapka",
    tone: "var(--destructive)",
    svg:
      '<path d="M23 31 C31 -1 62 -9 93 3 C100 12 102 22 101 32 Z" fill="currentColor" fill-opacity=".92"/>' +
      // lem = úzky pásik kopírujúci temeno; sedí NA fotke, aby čiapka vyzerala oblečená
      '<path d="M16 34 Q65 12 114 34 Q65 30 16 34 Z" fill="var(--flair-shine)" fill-opacity=".97" stroke="var(--flair-shine)" stroke-width="2.4"/>' +
      '<circle cx="95" cy="6" r="9" fill="var(--flair-shine)" fill-opacity=".95" stroke="var(--flair-shine)"/>',
  },
  flair_sombrero: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Sombrero",
    tone: "var(--type-vyzva)",
    svg:
      '<path d="M3 43 Q65 21 127 43 Q65 63 3 43 Z" fill="currentColor" fill-opacity=".8"/>' +
      '<path d="M40 44 Q40 7 65 7 Q90 7 90 44" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M38 39 Q65 47 92 39" stroke="var(--flair-ink)" stroke-width="6" opacity=".55"/>',
  },
  flair_beanie: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Zimná čiapka",
    tone: "var(--info)",
    svg:
      '<path d="M26 37 Q28 8 65 8 Q102 8 104 37 Z" fill="currentColor" fill-opacity=".92"/>' +
      // lem ako pri Mikulášskej — úzky pásik na temene, nie blok cez čelo
      '<path d="M18 36 Q65 16 112 36 Q65 32 18 36 Z" fill="var(--flair-shine)" fill-opacity=".97" stroke="var(--flair-shine)" stroke-width="2.4"/>' +
      '<circle cx="65" cy="4" r="8" fill="var(--flair-shine)" fill-opacity=".95" stroke="var(--flair-shine)"/>',
  },
  flair_cap: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Šiltovka",
    tone: "var(--brand-primary)",
    svg:
      // kupola
      '<path d="M28 38 Q30 7 65 7 Q100 7 102 38 Z" fill="currentColor" fill-opacity=".95"/>' +
      // šilt — plná farba stmavená tmavým tokenom, aby sa odlíšil od kupoly (nie priehľadnosťou)
      '<path d="M100 32 Q128 33 130 44 Q110 48 96 41 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M100 32 Q128 33 130 44 Q110 48 96 41 Z" fill="var(--flair-ink)" fill-opacity=".38" stroke="none"/>' +
      // predný panel a šev
      '<path d="M65 8 V38" stroke="var(--flair-shine)" stroke-width="2" opacity=".55"/>' +
      '<circle cx="65" cy="10" r="3.2" fill="var(--flair-shine)" stroke="none"/>',
  },
  flair_tophat: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Cylinder",
    tone: "var(--flair-ink)",
    svg:
      '<path d="M12 38 Q65 29 118 38 Q65 48 12 38 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M36 38 V6 Q65 0 94 6 V38 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M36 29 Q65 35 94 29" stroke="var(--tier-gold)" stroke-width="6"/>',
  },
  flair_cowboy: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Kovbojský klobúk",
    tone: "var(--tier-bronze)",
    svg:
      '<path d="M7 41 Q21 32 35 35 Q65 45 95 35 Q109 32 123 41 Q98 53 65 53 Q32 53 7 41 Z" fill="currentColor" fill-opacity=".8"/>' +
      '<path d="M35 38 Q37 8 65 8 Q93 8 95 38" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M34 33 Q65 41 96 33" stroke="var(--flair-ink)" stroke-width="6" opacity=".6"/>',
  },
  flair_hatter: {
    scale: 0.72, lift: 7,   // ako ostatné klobúky — sedí NA kruhu profilovky
    label: "Klobúk šialeného klobučníka",
    tone: "var(--flair-ink)",
    svg:
      // vysoký cylinder so širokou stuhou a cedulkou — poznávacie znamenie klobučníka
      '<path d="M10 40 Q65 27 120 40 Q65 55 10 40 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M28 40 V4 Q65 -4 102 4 V40 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M28 26 Q65 34 102 26 L102 36 Q65 44 28 36 Z" fill="var(--tier-bronze)" fill-opacity=".95" stroke="none"/>' +
      '<path d="M88 9 L101 11 L99 24 L86 22 Z" fill="var(--flair-shine)" fill-opacity=".95" stroke="none"/>',
  },
  flair_wizard: {
    // Vlastný `scale`/`lift` (nie 0.72/7 ako ostatné čiapky) — zadanie majiteľky:
    // klobúk má SEDIEŤ NA kruhu profilovky, nie byť natiahnutý cez tvár, a hore má byť
    // mäsitý, nie tenká ihla. `lift: 22` posadí striešku na temeno (y' ≈ 18, temeno je 15)
    // a kužeľ preto vyčnieva NAD SVG box — to je v poriadku, kreslí sa s `overflow: visible`
    // a milestone flair v `Avatar.jsx` sedí ešte vyššie (−30 % veľkosti avatara).
    // Prepočet pri zmene kresby: y' = 40 + (y − 40)·scale − lift (temeno hlavy = 15).
    scale: 0.62, lift: 22,
    label: "Čarodejnícky klobúk",
    tone: "var(--type-sutaz)",
    svg:
      // Telo AJ prevesený hrot sú JEDEN súvislý obrys — skladané z viacerých tvarov (či už
      // výplní alebo rúrok) sa na veľkom avatare v profile rozpadli na viditeľné kusy.
      // Obrys ide: ľavá hrana tela hore → cez vrch doľava → vonkajšia hrana previsu nadol →
      // okrúhly koniec → spodná hrana previsu späť k telu → pravá hrana tela dolu.
      '<path d="M30 41 C31 18 36 -2 44 -16 C50 -26 44 -34 31 -34 C19 -34 9 -25 9 -12 ' +
      'C9 -4 14 -1 19 -4 C25 -8 26 -16 34 -20 C42 -24 51 -22 56 -16 C62 -8 69 12 76 41 Z" fill="currentColor" fill-opacity=".95"/>' +
      // záhyb: náznak plsti, aby telo nepôsobilo ploško
      '<path d="M46 30 C48 10 52 -6 58 -17" stroke="var(--flair-ink)" stroke-width="2.6" opacity=".16"/>' +
      // strieška: široká a zvlnená, s naklonením (pravá strana vyššie) ako na predlohe
      '<path d="M2 38 Q14 26 34 31 Q52 38 70 34 Q90 28 108 24 Q124 22 128 32 Q116 50 82 55 Q46 60 20 51 Q6 46 2 38 Z" fill="currentColor" fill-opacity=".95"/>' +
      // stuha: široký zlatý pás (predloha nemá sponu, ale mohutnú stuhu)
      '<path d="M28 26 Q56 37 84 23 L86 32 Q56 46 28 34 Z" fill="var(--tier-gold)" fill-opacity=".95" stroke="none"/>' +
      '<path d="M28 32 Q56 43 85 29" stroke="var(--flair-ink)" stroke-width="2" opacity=".18"/>',
  },
  // v30.3 — KOSTÝMOVÉ kúsky (zadanie majiteľky, podľa dodaných predlôh). Sú väčšie než
  // zvyšok katalógu a kreslia sa v plnej mierke (bez `scale`/`lift`), lebo vlasy/dredy/telo
  // idú AJ POD úroveň temena — okolo fotky. Časti označené `{behind}` sa orežú maskou hlavy,
  // takže vyzerajú, že sú za profilovkou (inak by vlasy ležali cez tvár).
  // Viacfarebné kúsky (rasta, vrtuľová) miešajú sémantické tokeny rovnako ako `rainbowDef`
  // pri jednorožcovi — je to zámerná výnimka z „odvoď od --brand-primary" (§6a bod 3):
  // rasta trikolóra ani dúhová čiapka nedávajú v inej farbe zmysel.
  flair_rasta: {
    label: "Rasta čiapka s dredami",
    tone: "var(--success)",
    svg:
      // dredy — tri pramene na každej strane, za fotkou
      '<path d="M33 29 C15 44 6 68 8 100" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      '<path d="M45 33 C29 52 20 78 23 108" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      '<path d="M57 37 C45 60 39 84 42 112" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      '<path d="M97 29 C115 44 124 68 122 100" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      '<path d="M85 33 C101 52 110 78 107 108" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      '<path d="M73 37 C85 60 91 84 88 112" stroke="var(--flair-ink)" stroke-width="8" stroke-linecap="round" {behind}/>' +
      // pletená čiapka: zelený základ, žltý pás, červený vrch
      '<path d="M20 36 C20 6 110 6 110 36 Q65 46 20 36 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M23 27 C38 34 92 34 107 27 C106 22 104 18 101 14 C86 22 44 22 29 14 C26 18 24 22 23 27 Z" fill="var(--stat-sun)" fill-opacity=".95" stroke="none"/>' +
      '<path d="M31 12 C40 3 90 3 99 12 C86 20 44 20 31 12 Z" fill="var(--destructive)" fill-opacity=".95" stroke="none"/>',
  },
  flair_propeller: {
    // vrtuľa sa TOČÍ — trieda `bz-flair-spin` (animácia v styles.css, s prefers-reduced-motion
    // guardom). Preto je kresba v plnej mierke: `transform-origin` vrtule je v pevných
    // súradniciach viewBoxu (65, 9) a `scale` by ho posunul. Kupola preto sedí nižšie,
    // nech má vrtuľa nad ňou miesto a nekreslí sa cez čiapku.
    label: "Vrtuľová čiapka",
    tone: "var(--info)",
    svg:
      // dúhová kupola — štyri diely
      '<path d="M26 45 C26 25 42 17 65 17 V45 Z" fill="var(--destructive)" fill-opacity=".95"/>' +
      '<path d="M46 20 C52 18 58 17 65 17 V45 H46 Z" fill="var(--stat-flame)" fill-opacity=".95"/>' +
      '<path d="M65 17 C78 17 88 20 94 26 L94 45 H65 Z" fill="var(--stat-sun)" fill-opacity=".95"/>' +
      '<path d="M94 26 C100 31 104 37 104 45 H94 Z" fill="var(--success)" fill-opacity=".95"/>' +
      // šilt
      '<path d="M102 40 Q126 41 128 50 Q110 55 96 48 Z" fill="var(--success)" fill-opacity=".95"/>' +
      // stopka od kupoly k náboju
      '<path d="M65 17 V11" stroke="var(--flair-ink)" stroke-width="3"/>' +
      // vrtuľa — točí sa okolo náboja (65, 9)
      '<g class="bz-flair-spin">' +
      '<path d="M62 9 C50 3 38 3 31 7 C38 13 50 14 62 9 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<path d="M68 9 C80 15 92 15 99 11 C92 5 80 4 68 9 Z" fill="currentColor" fill-opacity=".95"/>' +
      '<circle cx="65" cy="9" r="3.6" fill="var(--tier-gold)" fill-opacity=".95"/>' +
      '</g>',
  },
  flair_shark: {
    // Žralok, ktorý práve zhltol profilovku: papuľa je otvorená PRESNE na veľkosť fotky
    // (horná čeľusť nad ňou, spodná pod ňou, zuby zahryznuté do okraja) a zvyšok tela —
    // chrbtová plutva, žiabre, prsná plutva a chvost — trčí VPRAVO od fotky.
    // Telo aj hlava sa kreslia s `{behind}`, takže ich maska hlavy oreže a pôsobia, akoby
    // boli ZA fotkou; čeľuste idú navrch. Tvár preto ostáva voľná — prekryjú ju len zuby
    // po obvode. ŽIADNY `scale`/`lift`: papuľa musí sedieť na kruh avatara 1:1.
    // Oblúky čeľustí a zuby sú vypočítané v uhlovom systéme okolo stredu (65,65) —
    // needituj čísla po jednom, prekresli ich (postup v komentári na začiatku súboru).
    label: "Žraločia hlava",
    tone: "var(--tier-silver)",
    svg:
      // telo, plutvy a žiabre ZA fotkou (maska hlavy ich oreže na kruhu profilovky)
      '<g {behind}>' +
      // chrbtová plutva
      '<path d="M104 20 L126 -13 L133 29 Z" fill="currentColor" fill-opacity=".95"/>' +
      // prsná plutva
      '<path d="M100 100 L117 123 L119 95 Z" fill="currentColor" fill-opacity=".8"/>' +
      // hlava → telo → chvost (jeden obrys)
      '<path d="M40 26 C64 4 96 2 114 16 C126 26 131 42 131 57 L152 22 C148 40 142 54 135 63 C143 74 147 88 146 '
      + '104 C140 92 134 80 130 70 C128 86 118 98 104 106 C80 118 52 106 44 84 Z" fill="currentColor" fill-opacity=".95"/>' +
      // žiabre
      '<path d="M114 40 C116 45 117 50 116 55M120 46 C122 51 123 56 122 61M126 53 C128 58 128 63 127 68" fill="none" stroke="var(--flair-ink)" stroke-width="2" opacity=".2"/>' +
      '</g>' +
      // horná čeľusť + zuby (NAD fotkou)
      '<path d="M 33.6 31.4 L 34.4 26 L 36.6 21.6 L 39.5 17.6 L 43 14 L 46.9 10.8 L 51.2 8 L 55.9 5.6 L 61 3.7 L '
      + '66.2 2.9 L 71.6 3 L 76.9 3.5 L 82.2 4.4 L 87.5 5.8 L 92.6 7.5 L 97.6 9.8 L 102.5 12.4 L 107.1 '
      + '15.5 L 111.6 18.9 L 115.7 22.7 L 119.5 27 L 123 31.5 L 104.8 42 L 97.4 41.1 L 98.7 33.7 L 90.9 '
      + '34.9 L 90.9 27 L 83.2 30.4 L 81.8 22.2 L 74.9 27.8 L 71.8 19.5 L 66.4 27.1 L 61.5 19.1 L 58 28.3 '
      + 'L 51.4 21.1 L 50.2 31.3 L 41.9 25.2 L 43.4 35.9 L 33.6 31.4 Z" fill="currentColor" fill-opacity=".97"/>' +
      // spodná čeľusť + zuby (NAD fotkou)
      '<path d="M 29.5 95.8 L 30.7 100.5 L 32.9 104.5 L 35.7 108.2 L 39 111.6 L 42.7 114.6 L 46.7 117.3 L 51.1 '
      + '119.6 L 55.7 121.5 L 60.5 122.8 L 65.6 123.2 L 70.6 123.2 L 75.7 122.8 L 80.7 122 L 85.7 120.8 L '
      + '90.6 119.1 L 95.4 117.1 L 100 114.6 L 104.4 111.7 L 108.6 108.4 L 112.6 104.8 L 116.2 100.8 L '
      + '119.6 96.5 L 105.7 88.5 L 98 90.6 L 97.9 98.6 L 89.6 98 L 87.7 106.2 L 79.6 102.9 L 75.8 110.7 L '
      + '68.8 104.8 L 63.1 112 L 58.1 103.8 L 50.6 109.7 L 48.2 100 L 39.1 104.2 L 39.8 93.8 L 29.5 95.8 '
      + 'Z" fill="currentColor" fill-opacity=".97"/>' +
      // biele zuby hornej čeľuste
      '<path d="M 106.7 40.9 L 97.4 41.1 L 100.3 32.2 Z M 100.3 32.2 L 90.9 34.9 L 92.1 25.2 Z M 92.1 25.2 L '
      + '83.2 30.4 L 82.6 20.1 Z M 82.6 20.1 L 74.9 27.8 L 72.1 17.3 Z M 72.1 17.3 L 66.4 27.1 L 61.3 '
      + '16.9 Z M 61.3 16.9 L 58 28.3 L 50.7 19 Z M 50.7 19 L 50.2 31.3 L 40.8 23.3 Z M 40.8 23.3 L 43.4 '
      + '35.9 L 32.1 29.7 Z" fill="var(--flair-shine)" fill-opacity=".97" stroke="var(--flair-shine)" stroke-width="1.2"/>' +
      // biele zuby spodnej čeľuste
      '<path d="M 107.6 89.6 L 98 90.6 L 99.4 100.1 Z M 99.4 100.1 L 89.6 98 L 88.7 108.1 Z M 88.7 108.1 L 79.6 '
      + '102.9 L 76.3 112.9 Z M 76.3 112.9 L 68.8 104.8 L 63 114.2 Z M 63 114.2 L 58.1 103.8 L 49.9 111.8 '
      + 'Z M 49.9 111.8 L 48.2 100 L 37.9 106.1 Z M 37.9 106.1 L 39.8 93.8 L 27.9 97.3 Z" fill="var(--flair-shine)" fill-opacity=".97" stroke="var(--flair-shine)" stroke-width="1.2"/>' +
      // oko — na hornej čeľusti, mimo kruhu fotky
      '<circle cx="46" cy="13" r="5.5" fill="var(--flair-shine)" fill-opacity=".97"/>' +
      '<circle cx="46" cy="13" r="2.4" fill="var(--flair-ink)" stroke="none"/>',
  },
  flair_chef: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Kuchárska čiapka",
    tone: "var(--flair-ink)",
    svg:
      '<path d="M33 35 V17 Q33 3 47 5 Q53 -3 65 0 Q77 -3 83 5 Q97 3 97 17 V35 Z" fill="var(--flair-shine)" fill-opacity=".97"/>' +
      '<path d="M30 35 H100 Q104 35 104 39 V43 Q104 47 100 47 H30 Q26 47 26 43 V39 Q26 35 30 35 Z" fill="var(--flair-shine)" fill-opacity=".97"/>',
  },
  flair_graduate: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Absolventská čiapka",
    tone: "var(--info)",
    svg:
      '<path d="M65 7 L118 26 L65 45 L12 26 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M41 33 V45 Q65 56 89 45 V33" fill="currentColor" fill-opacity=".78"/>' +
      '<path d="M117 27 V49"/><circle cx="117" cy="53" r="4" fill="currentColor" fill-opacity=".35"/>',
  },
  flair_viking: {
    scale: 0.72, lift: 7,   // v30 — menšie a vyššie, nech sedia NA kruhu profilovky
    label: "Vikingská prilba",
    tone: "var(--tier-silver)",
    svg:
      '<path d="M31 39 Q31 8 65 8 Q99 8 99 39 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M65 9 V38"/>' +
      '<path d="M31 30 C15 27 4 15 6 2 C21 6 31 15 33 27 Z" fill="currentColor" fill-opacity=".8"/>' +
      '<path d="M99 30 C115 27 126 15 124 2 C109 6 99 15 97 27 Z" fill="currentColor" fill-opacity=".8"/>',
  },

  // ── uši ───────────────────────────────────────────────────────────────────
  flair_catears: {
    scale: 0.88,
    label: "Mačacie uši",
    tone: "var(--stat-flame)",
    svg:
      '<path d="M27 43 L31 8 L58 27 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M103 43 L99 8 L72 27 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M34 36 L36 19 L48 30 Z" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>' +
      '<path d="M96 36 L94 19 L82 30 Z" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>',
  },
  flair_mouseears: {
    scale: 0.88,
    label: "Myšacie uši",
    tone: "var(--flair-ink)",
    svg:
      '<circle cx="26" cy="24" r="19" fill="currentColor" fill-opacity=".92"/>' +
      '<circle cx="104" cy="24" r="19" fill="currentColor" fill-opacity=".92"/>',
  },
  flair_bunnyears: {
    scale: 0.88,
    label: "Zajačie uši",
    tone: "var(--type-sutaz)",
    svg:
      '<path d="M50 39 C42 17 46 1 55 1 C64 1 63 19 61 39 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M80 39 C88 17 84 1 75 1 C66 1 67 19 69 39 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M53 33 C50 18 52 8 55 8 C58 8 58 21 57 33 Z" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>' +
      '<path d="M77 33 C80 18 78 8 75 8 C72 8 72 21 73 33 Z" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>',
  },
  flair_bearears: {
    scale: 0.88,
    label: "Medvedie uši",
    tone: "var(--tier-bronze)",
    svg:
      '<circle cx="34" cy="21" r="15" fill="currentColor" fill-opacity=".92"/>' +
      '<circle cx="96" cy="21" r="15" fill="currentColor" fill-opacity=".92"/>' +
      '<circle cx="34" cy="21" r="7" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>' +
      '<circle cx="96" cy="21" r="7" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>',
  },
  flair_monkeyears: {
    scale: 0.88,
    label: "Opičie uši",
    tone: "var(--tier-bronze)",
    svg:
      '<circle cx="10" cy="57" r="16" fill="currentColor" fill-opacity=".92"/>' +
      '<circle cx="120" cy="57" r="16" fill="currentColor" fill-opacity=".92"/>' +
      '<circle cx="12" cy="57" r="8" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>' +
      '<circle cx="118" cy="57" r="8" fill="var(--flair-shine)" fill-opacity=".92" stroke="none"/>',
  },

  // ── rohy a parohy ─────────────────────────────────────────────────────────
  flair_horns: {
    scale: 0.88,
    label: "Rožky",
    tone: "var(--destructive)",
    svg:
      '<path d="M35 34 C26 20 30 7 45 2 C40 13 42 25 49 31 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M95 34 C104 20 100 7 85 2 C90 13 88 25 81 31 Z" fill="currentColor" fill-opacity=".92"/>',
  },
  flair_unicorn: {
    // Zámerne NEsmeruje hore, ale šikmo do PRAVEJ strany (zadanie majiteľky).
    scale: 0.82,
    label: "Jednorožcov roh",
    tone: "var(--type-sutaz)",
    rainbow: true,   // dúhová výplň — `{grad}` sa nahradí odkazom na prechod (viď HeadFlair)
    svg:
      '<path d="M86 37 L128 2 L99 46 Z" fill="{grad}"/>' +
      '<path d="M95 34 L103 39" stroke="var(--flair-shine)"/>' +
      '<path d="M105 22 L112 28" stroke="var(--flair-shine)"/>' +
      '<path d="M115 12 L121 18" stroke="var(--flair-shine)"/>',
  },
  flair_antlers: {
    scale: 0.88,
    label: "Sobie parohy",
    tone: "var(--tier-bronze)",
    svg:
      '<path d="M41 34 C35 21 31 11 26 3"/><path d="M35 19 L18 12"/><path d="M30 9 L16 3"/>' +
      '<path d="M89 34 C95 21 99 11 104 3"/><path d="M95 19 L112 12"/><path d="M100 9 L114 3"/>',
  },

  // ── venčeky a efekty nad hlavou ───────────────────────────────────────────
  flair_flowers: {
    scale: 0.72, lift: 7,
    label: "Kvetinový venček",
    tone: "var(--success)",
    // vyfarbené v štýle hviezdičiek: plný kvet + svetlý stred (nie ten istý odtieň s inou priehľadnosťou)
    svg:
      '<path d="M21 41 Q65 5 109 41" stroke-width="4"/>' +
      '<circle cx="23" cy="38" r="7" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<circle cx="40" cy="20" r="7" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<circle cx="65" cy="11" r="8" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<circle cx="90" cy="20" r="7" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<circle cx="107" cy="38" r="7" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<circle cx="23" cy="38" r="3" fill="var(--flair-shine)" stroke="none"/>' +
      '<circle cx="40" cy="20" r="3" fill="var(--flair-shine)" stroke="none"/>' +
      '<circle cx="65" cy="11" r="3.4" fill="var(--flair-shine)" stroke="none"/>' +
      '<circle cx="90" cy="20" r="3" fill="var(--flair-shine)" stroke="none"/>' +
      '<circle cx="107" cy="38" r="3" fill="var(--flair-shine)" stroke="none"/>',
  },
  flair_antennae: {
    scale: 0.88,
    label: "Tykadlá",
    tone: "var(--success)",
    svg:
      '<path d="M48 35 C42 21 40 11 34 5"/><circle cx="31" cy="3" r="6" fill="var(--flair-shine)" fill-opacity=".92"/>' +
      '<path d="M82 35 C88 21 90 11 96 5"/><circle cx="99" cy="3" r="6" fill="var(--flair-shine)" fill-opacity=".92"/>',
  },
  flair_stars: {
    scale: 0.88,
    label: "Hviezdičky",
    tone: "var(--stat-sun)",
    glow: true,   // hviezdy majú svietiť — rovnaká aura ako svätožiara
    svg:
      '<path d="M65 1 C65 8 68 11 77 13 C68 15 65 18 65 25 C65 18 62 15 53 13 C62 11 65 8 65 1 Z" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<path d="M65 6 C65 10 66.5 11.5 71 13 C66.5 14.5 65 16 65 20 C65 16 63.5 14.5 59 13 C63.5 11.5 65 10 65 6 Z" fill="var(--flair-shine)" stroke="none"/>' +
      '<path d="M34 20 C34 25 36 27 43 29 C36 31 34 33 34 38 C34 33 32 31 25 29 C32 27 34 25 34 20 Z" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<path d="M34 24 C34 27 35 28 38.5 29 C35 30 34 31 34 34 C34 31 33 30 29.5 29 C33 28 34 27 34 24 Z" fill="var(--flair-shine)" stroke="none"/>' +
      '<path d="M96 20 C96 25 98 27 105 29 C98 31 96 33 96 38 C96 33 94 31 87 29 C94 27 96 25 96 20 Z" fill="currentColor" fill-opacity=".95" stroke-width="2"/>' +
      '<path d="M96 24 C96 27 97 28 100.5 29 C97 30 96 31 96 34 C96 31 95 30 91.5 29 C95 28 96 27 96 24 Z" fill="var(--flair-shine)" stroke="none"/>',
  },
  flair_cloud: {
    scale: 0.85,
    label: "Mráčik",
    tone: "var(--info)",
    svg:
      '<path d="M40 38 Q34 25 47 21 Q51 6 68 8 Q83 9 85 22 Q95 26 90 38 Z" fill="currentColor" fill-opacity=".92"/>' +
      '<path d="M47 30 Q52 20 64 21 Q74 22 76 30 Z" fill="var(--flair-shine)" fill-opacity=".55" stroke="none"/>' +
      '<path d="M53 43 L50 51" stroke="var(--flair-shine)"/>' +
      '<path d="M65 43 L62 51" stroke="var(--flair-shine)"/>' +
      '<path d="M77 43 L74 51" stroke="var(--flair-shine)"/>',
  },
};

export const HEAD_FLAIR_CODES = Object.keys(HEAD_FLAIRS);
// len ozdoby slotu HEAD — `Avatar` podľa toho rozlišuje, čo patrí na hlavu a čo navrch
export const isOverFlair = (code) => !!(HEAD_FLAIRS[code] && HEAD_FLAIRS[code].slot === "over");

/**
 * Ozdoba na hlave.
 *  - `size`  = veľkosť avatara v px (SVG je 130 % z nej, posunuté o −15 %, takže
 *              sedí na kruh avatara v každej veľkosti).
 *  - `preview` = samostatná ukážka (Obchod, dizajnová kuchyňa): rovnaká kresba,
 *              len s naznačeným kruhom hlavy a bez absolútneho pozicovania.
 */
export function HeadFlair({ code, size = 36, preview = false }) {
  const maskId = useId().replace(/:/g, "");   // useId dáva ":r0:", v url(#…) by dvojbodka vadila
  const def = HEAD_FLAIRS[code];
  if (!def) return null;
  const box = Math.round(size * 1.3);
  const common = {
    viewBox: `0 0 ${HEAD.size} ${HEAD.size}`,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (preview) {
    return (
      <svg {...common} width={box} height={box} aria-label={def.label} role="img"
        style={{ color: def.tone, overflow: "visible", display: "block",
          ...(def.glow ? { filter: `drop-shadow(0 0 2px var(--flair-shine)) drop-shadow(0 0 5px ${def.tone})` } : {}) }}>
        <title>{def.label}</title>
        <circle cx={HEAD.cx} cy={HEAD.cy} r={HEAD.r} fill="none"
          stroke="var(--neutral-border-strong)" strokeWidth="3" strokeDasharray="6 7" />
        <g dangerouslySetInnerHTML={{ __html: body(def, maskId) }} />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true" className={def.glow ? "bz-halo-glow" : undefined}
      style={{
        position: "absolute", left: "-15%", top: "-15%", width: "130%", height: "130%",
        color: def.tone, overflow: "visible", pointerEvents: "none",
        // v30 — ozdoby s `glow` (svätožiara) dostanú auru vo VLASTNEJ farbe navyše k tieňu.
        // Na Androide (Mali) sa vrství menej — viacnásobný filter tam vie blikať (CLAUDE.md §6).
        filter: def.glow
          ? `drop-shadow(0 0 2px var(--flair-shine)) drop-shadow(0 0 4px ${def.tone}) drop-shadow(0 0 9px ${def.tone})`
          : "drop-shadow(0 1px 1.5px var(--scrim))",
      }}
      dangerouslySetInnerHTML={{ __html: body(def, maskId) }} />
  );
}
